import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Login({ onLogin }) {
  const [role, setRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      let googleUserInfo = null;
      try {
        setLoading(true);
        setError('');
        
        // Get user info from Google
        const userInfoResponse = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          }
        );

        googleUserInfo = userInfoResponse.data;

        // Authenticate with our backend
        const response = await axios.post(`${BACKEND_URL}/api/auth/google`, {
          role: role,
          googleInfo: googleUserInfo
        });

        onLogin(response.data.user);
      } catch (err) {
        console.error('Login error:', err);
        if (err.response) {
          const detail = err.response.data.detail;
          
          // Handle teacher authorization errors
          if (detail === 'teacher_not_approved') {
            setError('No tienes permiso para acceder como profesor. ¿Deseas solicitar acceso?');
            // Store user info for potential request
            if (googleUserInfo) {
              localStorage.setItem('pendingTeacherRequest', JSON.stringify(googleUserInfo));
            }
            setTimeout(() => {
              window.location.href = '/teacher-request';
            }, 2000);
          } else if (detail === 'teacher_pending') {
            setError('Tu solicitud para ser profesor está pendiente de aprobación. Por favor espera.');
          } else {
            setError(`Error: ${detail || 'Error al iniciar sesión'}`);
          }
        } else {
          setError('Error al iniciar sesión. Por favor intenta de nuevo.');
        }
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      setError('Error al autenticar con Google');
    },
    scope: 'openid profile email',
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-purple-700 to-pink-600 p-4" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-6xl font-bold text-white mb-2" data-testid="app-title">QuizMaster</h1>
          <p className="text-xl text-purple-200">Plataforma Educativa Interactiva</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8" data-testid="login-card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Iniciar Sesión</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
              {error}
            </div>
          )}

          {/* Role Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Selecciona tu rol:</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setRole('student')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  role === 'student'
                    ? 'border-primary bg-primary text-white shadow-lg scale-105'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary'
                }`}
                data-testid="role-student-btn"
              >
                <div className="text-3xl mb-2">🎓</div>
                <div className="font-semibold">Estudiante</div>
              </button>
              <button
                onClick={() => setRole('teacher')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  role === 'teacher'
                    ? 'border-primary bg-primary text-white shadow-lg scale-105'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary'
                }`}
                data-testid="role-teacher-btn"
              >
                <div className="text-3xl mb-2">👨‍🏫</div>
                <div className="font-semibold">Profesor</div>
              </button>
            </div>
          </div>

          {/* Google Login Button */}
          <button
            onClick={() => login()}
            disabled={loading}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="google-login-btn"
          >
            {loading ? (
              'Cargando...'
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar con Google
              </>
            )}
          </button>

          <p className="mt-6 text-center text-sm text-gray-600">
            Al continuar, aceptas nuestros{' '}
            <a 
              href="/condiciones.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-purple-700 underline font-medium"
            >
              términos y condiciones
            </a>
            {' '}y nuestra{' '}
            <a 
              href="/privacidad.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-purple-700 underline font-medium"
            >
              política de privacidad
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
